import React, { useMemo, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';

const REGION = 'us-central1';

const SA_PROVINCES = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo',
  'Mpumalanga', 'Northern Cape', 'North West', 'Western Cape'
];

const APP_OPTIONS = [
  { id: "cashup", label: "Cash-Up Admin" },
  { id: "morpheus", label: "Morpheus Count" },
  { id: "ontime", label: "On Time" },
];

function normalizeAppId(app) {
  const v = String(app || "").trim().toLowerCase();
  if (["neo", "admin", "cash-up"].includes(v)) return "cashup";
  if (["mc", "morphius", "morpheus-count"].includes(v)) return "morpheus";
  return v;
}

function normalizeApps(input) {
  if (Array.isArray(input)) return [...new Set(input.map(normalizeAppId).filter(Boolean))];
  if (input && typeof input === "object") {
    return [...new Set(Object.entries(input).filter(([, v]) => v === true).map(([k]) => normalizeAppId(k)).filter(Boolean))];
  }
  return [];
}

function appsArrayToMap(arr) {
  const selected = new Set(normalizeApps(arr));
  return APP_OPTIONS.reduce((out, app) => {
    out[app.id] = selected.has(app.id);
    return out;
  }, {});
}


function emailDocId(email) {
  return String(email || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'owner';
}

export default function SuperAdminDashboard() {
  const prefersOnTime = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return String(params.get('app') || '').toLowerCase() === 'ontime';
  }, []);
  // form
  const [orgName, setOrgName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [firstStore, setFirstStore] = useState('');
  const [province, setProvince] = useState('');
  const [businessMode, setBusinessMode] = useState('hospitality_retail');
  const [appVariant, setAppVariant] = useState('shared_device');
  const [apps, setApps] = useState(prefersOnTime ? ["ontime"] : ["cashup"]);
  const [defaultApp, setDefaultApp] = useState(prefersOnTime ? "ontime" : "cashup");

  // ui
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [link, setLink] = useState('');

  const toggleApp = (id) => {
    setApps(prev => {
      if (prev.includes(id)) {
        if (prev.length === 1) return prev; // keep at least one
        const next = prev.filter(x => x !== id);
        if (!next.includes(defaultApp)) setDefaultApp(next[0]);
        return next;
      }
      return [...prev, id];
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setMsg(''); setErr(''); setLink('');

    if (!orgName.trim() || !adminEmail.trim() || apps.length === 0) {
      setErr('Please fill all required fields and enable at least one app.');
      return;
    }

    setLoading(true);
    try {
      const fn = httpsCallable(getFunctions(undefined, REGION), 'createOrgAndAdmin');
      const payload = {
        // Backward-compatible fields (older function versions)
        orgName: orgName.trim(),
        adminEmail: adminEmail.trim(),
        adminName: adminName.trim() || undefined,
        firstStoreName: firstStore.trim() || undefined,
        province: province || undefined,
        timezone: 'Africa/Johannesburg',
        businessMode,
        appVariant,
        apps,
        defaultApp,
        // Preferred structured payload (newer validation expects org.name)
        org: {
          name: orgName.trim(),
          province: province || undefined,
          timezone: 'Africa/Johannesburg',
          businessMode,
          appVariant,
          enabledApps: apps,
          defaultApp,
        },
        adminUser: {
          email: adminEmail.trim(),
          displayName: adminName.trim() || undefined,
          role: 'owner',
        },
        firstStore: firstStore.trim() ? { name: firstStore.trim() } : undefined,
      };
      const res = await fn(payload);
      const { orgId, storeId, passwordSetLink } = res.data || {};

      if (orgId) {
        const safeApps = normalizeApps(apps).length ? normalizeApps(apps) : ["cashup"];
        const enabledAppsMap = appsArrayToMap(safeApps);
        const uid = String(res?.data?.uid || res?.data?.userId || res?.data?.authUid || res?.data?.adminUid || res?.data?.adminUser?.uid || res?.data?.member?.uid || "").trim();

        // Frontend mirror so newly created org details don't appear blank if backend omits fields.
        await setDoc(doc(db, 'orgs', orgId), {
          name: orgName.trim(),
          province: province || '',
          timezone: 'Africa/Johannesburg',
          apps: enabledAppsMap,
          enabledApps: safeApps,
          defaultApp: safeApps.includes(defaultApp) ? defaultApp : safeApps[0],
          businessMode,
          appVariant,
          updatedAt: serverTimestamp(),
          ownerEmail: adminEmail.trim().toLowerCase(),
          ownerDisplayName: adminName.trim() || '',
        }, { merge: true });

        // Frontend mirror for owner membership. Prefer the Firebase Auth UID doc,
        // because the app login guard reads orgs/{orgId}/members/{uid}.
        const ownerEmail = adminEmail.trim().toLowerCase();
        const ownerDisplayName = adminName.trim() || ownerEmail.split('@')[0] || 'Owner';
        const memberPayload = {
          uid: uid || '',
          authUid: uid || '',
          email: ownerEmail,
          emailLower: ownerEmail,
          displayName: ownerDisplayName,
          role: 'owner',
          apps: safeApps,
          appsMap: enabledAppsMap,
          appAccess: enabledAppsMap,
          enabledApps: enabledAppsMap,
          allStores: true,
          accessAllStores: true,
          storeIds: [],
          allowedStoreIds: [],
          visibleStoreIds: [],
          cashupStoreIds: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        if (uid) {
          await setDoc(doc(db, 'orgs', orgId, 'members', uid), memberPayload, { merge: true });
          // Keep a lightweight email-key mirror only for older tools/backfill.
          await setDoc(doc(db, 'orgs', orgId, 'members', emailDocId(ownerEmail)), {
            ...memberPayload,
            mirrorOf: uid,
            isEmailMirror: true,
          }, { merge: true });
        } else {
          await setDoc(doc(db, 'orgs', orgId, 'members', emailDocId(ownerEmail)), memberPayload, { merge: true });
        }
      }

      setMsg(`Organization created. Org ID: ${orgId}${storeId ? `, first store: ${storeId}` : ''}.`);
      if (passwordSetLink) setLink(passwordSetLink);

      // reset
      setOrgName(''); setAdminEmail(''); setAdminName(''); setFirstStore('');
      setProvince(''); setBusinessMode('hospitality_retail'); setAppVariant('shared_device'); setApps(prefersOnTime ? ['ontime'] : ['cashup']); setDefaultApp(prefersOnTime ? 'ontime' : 'cashup');
    } catch (e2) {
      setErr(e2?.message || 'Failed to create organization.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="super-create-page">
      <h2 className="text-xl font-semibold mb-4">Create Organization</h2>

      {err && <div className="mb-4 rounded border border-rose-400/30 bg-rose-500/10 text-rose-200 px-3 py-2">{err}</div>}
      {msg && <div className="mb-4 rounded border border-cyan-400/30 bg-cyan-500/10 text-cyan-200 px-3 py-2">{msg}</div>}
      {link && (
        <div className="mb-4 rounded border border-white/10 bg-white/5 px-3 py-2">
          <div className="text-sm text-white/80 mb-1">Password setup link for the new admin:</div>
          <a className="text-cyan-300 break-all underline" href={link} target="_blank" rel="noreferrer">{link}</a>
        </div>
      )}

      <form onSubmit={submit} className="grid gap-4 bg-white/5 border border-white/10 rounded-xl p-4">
        <div>
          <label className="block text-sm text-white/80 mb-1">Organization name*</label>
          <input
            className="w-full rounded bg-black/30 border border-white/10 px-3 py-2 outline-none focus:ring-2 ring-cyan-600"
            value={orgName} onChange={e=>setOrgName(e.target.value)} placeholder="e.g., The Spot Cape Town"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-white/80 mb-1">Admin email*</label>
            <input
              type="email"
              className="w-full rounded bg-black/30 border border-white/10 px-3 py-2 outline-none focus:ring-2 ring-cyan-600"
              value={adminEmail} onChange={e=>setAdminEmail(e.target.value)} placeholder="admin@example.com"
            />
          </div>
          <div>
            <label className="block text-sm text-white/80 mb-1">Admin display name (optional)</label>
            <input
              className="w-full rounded bg-black/30 border border-white/10 px-3 py-2 outline-none focus:ring-2 ring-cyan-600"
              value={adminName} onChange={e=>setAdminName(e.target.value)} placeholder="Org Owner"
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-white/80 mb-1">First store name (optional) — creates a store in Cash-Up Admin</label>
            <input
              className="w-full rounded bg-black/30 border border-white/10 px-3 py-2 outline-none focus:ring-2 ring-cyan-600"
              value={firstStore} onChange={e=>setFirstStore(e.target.value)} placeholder="CBD Town"
            />
          </div>
          <div>
            <label className="block text-sm text-white/80 mb-1">Province (South Africa)</label>
            <select
              className="w-full rounded bg-black/30 border border-white/10 px-3 py-2 outline-none focus:ring-2 ring-cyan-600"
              value={province} onChange={e=>setProvince(e.target.value)}
            >
              <option value="">Select province…</option>
              {SA_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <p className="text-xs text-white/50 mt-1">
              Timezone is fixed to <code>Africa/Johannesburg</code>.
            </p>
          </div>
        </div>


        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-white/80 mb-1">Business mode</label>
            <select
              className="w-full rounded bg-black/30 border border-white/10 px-3 py-2 outline-none focus:ring-2 ring-cyan-600"
              value={businessMode} onChange={e=>{
                const next = e.target.value;
                setBusinessMode(next);
                setAppVariant(['security_services', 'cleaning_services', 'field_services'].includes(next) ? 'personal_device' : 'shared_device');
              }}
            >
              <option value="hospitality_retail">Hospitality / Retail</option>
              <option value="security_services">Security Services</option>
              <option value="cleaning_services">Cleaning Services</option>
              <option value="field_services">Field Services</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-white/80 mb-1">App variant</label>
            <select
              className="w-full rounded bg-black/30 border border-white/10 px-3 py-2 outline-none focus:ring-2 ring-cyan-600"
              value={appVariant} onChange={e=>setAppVariant(e.target.value)}
            >
              <option value="shared_device">Shared Device</option>
              <option value="personal_device">Personal Device</option>
            </select>
          </div>
        </div>

        <div className="border-t border-white/10 pt-3">
          <div className="font-medium mb-2">Enabled apps*</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {APP_OPTIONS.map(a => {
              const checked = apps.includes(a.id);
              const disableUncheck = checked && apps.length === 1;
              return (
                <label key={a.id}
                  className={`flex items-center gap-2 rounded px-3 py-2 border border-white/10 ${checked ? 'bg-cyan-500/10' : 'bg-white/5'}`}
                  title={disableUncheck ? 'Keep at least one app' : ''}
                >
                  <input type="checkbox" checked={checked} disabled={disableUncheck}
                         onChange={()=>toggleApp(a.id)} />
                  <span>{a.label}</span>
                </label>
              );
            })}
          </div>

          <div className="mt-3">
            <label className="block text-sm text-white/80 mb-1">Default landing app*</label>
            <select
              className="w-full rounded bg-black/30 border border-white/10 px-3 py-2 outline-none focus:ring-2 ring-cyan-600"
              value={defaultApp} onChange={e=>setDefaultApp(e.target.value)}
            >
              {apps.map(a => <option key={a} value={a}>{APP_OPTIONS.find(x=>x.id===a)?.label || a}</option>)}
            </select>
          </div>
        </div>

        <div>
          <button disabled={loading}
                  className="px-4 py-2 rounded bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60">
            {loading ? 'Creating…' : 'Create Organization'}
          </button>
        </div>
      </form>
    </section>
  );
}
